import { useState } from 'react';

import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import FormControl from '@zenbusiness/zui/FormControl';

import FieldHeading from './FieldHeading';
import * as Styled from './SingleSelectField.style';
import TextInputField from './TextInputField';

const getSelectedInputOption = (def, value) => {
  const selectedOption = def.options.find((opt) => opt.id === value?.selectedId);
  return selectedOption?.type === 'input' ? selectedOption : null;
};

/**
 * Single select field using button-style options
 * @param {object} props - Component props
 * @param {object} props.def - Field definition
 * @param {string} props.value - Current selected value
 * @param {Function} props.onChange - Change handler
 * @param {boolean} [props.disabled=false] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 * @component
 */
function SingleSelectField({ def, value, onChange, disabled = false, error }) {
  // Track if selected option has input type
  const [selectedInputOption, setSelectedInputOption] = useState(
    getSelectedInputOption(def, value)
  );

  const handleOptionSelect = (optionId, optionLabel) => {
    const selectedOption = def.options.find((opt) => opt.id === optionId);

    if (selectedOption?.type === 'input') {
      setSelectedInputOption(selectedOption);
      // Send both selection and empty input value
      onChange({
        selectedId: optionId,
        inputValue: ''
      });
    } else {
      setSelectedInputOption(null);
      // Send just the id for regular options
      onChange({
        selectedId: optionId,
        value: optionLabel
      });
    }
  };

  // Handle input field changes
  const handleInputChange = (inputValue) => {
    if (selectedInputOption) {
      onChange({
        selectedId: selectedInputOption.id,
        inputValue
      });
    }
  };

  const fieldId = `single-select-${def.id}`;
  const hasError = Boolean(error);

  return (
    <FormControl
      component="fieldset"
      disabled={disabled}
      error={hasError}
      fullWidth
      required={def.required}
    >
      <FieldHeading
        description={def.description}
        label={def.label}
        labelId={`${fieldId}-label`}
        required={def.required}
      />

      <Box mt={2}>
        <Styled.OptionsContainer columns={def.columns || 1}>
          {def.options?.map((option) => {
            const isSelected = value?.selectedId === option.id;
            return (
              <Styled.OptionButton
                key={option.id}
                aria-checked={isSelected}
                disabled={disabled}
                isSelected={isSelected}
                role="radio"
                onClick={() => handleOptionSelect(option.id, option.label)}
              >
                <Styled.OptionContent>
                  <Styled.OptionLabel isSelected={isSelected}>{option.label}</Styled.OptionLabel>
                  {option.description && (
                    <Styled.OptionDescription>{option.description}</Styled.OptionDescription>
                  )}
                </Styled.OptionContent>
              </Styled.OptionButton>
            );
          })}
        </Styled.OptionsContainer>
      </Box>

      {/* Show input field when input-type option is selected */}
      {selectedInputOption && (
        <Box mt={2}>
          <TextInputField
            def={{
              id: `${fieldId}-input`,
              type: 'text',
              // Backwards compatible: if inputLabel is present, use it as label and placeholder as placeholder
              // Otherwise, fall back to using placeholder as label (existing behavior)
              label: selectedInputOption.inputLabel ?? selectedInputOption.placeholder,
              placeholder: selectedInputOption.inputLabel
                ? selectedInputOption.placeholder
                : undefined
            }}
            disabled={disabled}
            value={typeof value === 'object' ? value.inputValue : ''}
            onChange={handleInputChange}
          />
        </Box>
      )}

      {(error || def.helpText) && (
        <Styled.FieldHelperText>{error || def.helpText}</Styled.FieldHelperText>
      )}
    </FormControl>
  );
}

SingleSelectField.propTypes = {
  def: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['single_select']).isRequired,
    label: PropTypes.string,
    description: PropTypes.string,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    columns: PropTypes.oneOf([1, 2]), // Number of columns for options layout (default: 1)
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        description: PropTypes.string,
        type: PropTypes.oneOf(['input']), // Support for input type option
        // For input-type options:
        inputLabel: PropTypes.string, // Label displayed above the text input
        placeholder: PropTypes.string // Placeholder text inside the input (or label if inputLabel not set)
      })
    ).isRequired
  }).isRequired,
  // Update value to support both simple selection and input value
  value: PropTypes.oneOfType([
    PropTypes.string, // For regular option selection
    PropTypes.shape({
      selectedId: PropTypes.string.isRequired, // Selected option id
      inputValue: PropTypes.string // Text input value when type=input
    })
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string
};

export default SingleSelectField;
